import { DancerTrackType, GlobalTrackType, ITrackId, TrackType } from '../../models/Selection';
import {
	DancerPathColor,
	DancerPathNodeShape,
	IAudioClip,
	IAudioTrack,
	IDancer,
	IDancerPathNode,
	ITimeline,
	fixDancerPathNodesArray,
	isTrackIdEqual,
} from '../../models/Timeline';
import { feetToCentimeters } from '../../utils/general';
import { ActionType } from '../action-types';
import { Action } from '../actions';

const initialState: ITimeline = {
	dancers: [],
	musicTrack: {
		muted: false,
		clips: [],
	},
	ambienceTrack: {
		muted: false,
		clips: [],
	},
	foleyTrack: {
		muted: false,
		clips: [],
	},
	duration: 0,
	nextAudioClipId: 1,
	nextDancerId: 1,
	selectedAudioClip: null,
	selectedAudioGap: null,
	selectedDancerPathNode: null,
	selectedDancerId: null,
};

function mapAudioTracks(
	state: ITimeline,
	mapFunc: (trackId: ITrackId, audioTrack: IAudioTrack) => IAudioTrack
): ITimeline {
	let newState = {
		...state,
		musicTrack: mapFunc(
			{ trackType: TrackType.Global, type: GlobalTrackType.Music },
			state.musicTrack
		),
		ambienceTrack: mapFunc(
			{ trackType: TrackType.Global, type: GlobalTrackType.Ambience },
			state.ambienceTrack
		),
		foleyTrack: mapFunc(
			{ trackType: TrackType.Global, type: GlobalTrackType.Foley },
			state.foleyTrack
		),
		dancers: state.dancers.map((dancer) => {
			return {
				...dancer,
				aboveHeadTrack: mapFunc(
					{
						trackType: TrackType.Dancer,
						type: DancerTrackType.AboveHead,
						dancerId: dancer.id,
					},
					dancer.aboveHeadTrack
				),
				headTrack: mapFunc(
					{
						trackType: TrackType.Dancer,
						type: DancerTrackType.Head,
						dancerId: dancer.id,
					},
					dancer.headTrack
				),
				torsoTrack: mapFunc(
					{
						trackType: TrackType.Dancer,
						type: DancerTrackType.Torso,
						dancerId: dancer.id,
					},
					dancer.torsoTrack
				),
				legsTrack: mapFunc(
					{
						trackType: TrackType.Dancer,
						type: DancerTrackType.Legs,
						dancerId: dancer.id,
					},
					dancer.legsTrack
				),
				groundTrack: mapFunc(
					{
						trackType: TrackType.Dancer,
						type: DancerTrackType.Ground,
						dancerId: dancer.id,
					},
					dancer.groundTrack
				),
			};
		}),
	};
	return newState;
}

const differentTracks = (payloadTrackId: ITrackId, trackId: ITrackId) => {
	if (payloadTrackId.trackType !== trackId.trackType) return true;

	if (payloadTrackId.type !== trackId.type) return true;

	if (payloadTrackId.trackType === TrackType.Dancer && trackId.trackType === TrackType.Dancer) {
		if (payloadTrackId.dancerId !== trackId.dancerId) return true;

		return false;
	}

	return false;
};

function calculateTotalDuration(state: ITimeline) {
	let totalDuration = 0;

	function getTrackDuration(track: IAudioTrack): number {
		let duration = 0;
		for (const clip of track.clips) {
			duration = Math.max(duration, clip.time + clip.duration);
		}

		return duration;
	}

	totalDuration = Math.max(totalDuration, getTrackDuration(state.musicTrack));
	totalDuration = Math.max(totalDuration, getTrackDuration(state.ambienceTrack));
	totalDuration = Math.max(totalDuration, getTrackDuration(state.foleyTrack));

	for (const dancer of state.dancers) {
		totalDuration = Math.max(totalDuration, getTrackDuration(dancer.aboveHeadTrack));
		totalDuration = Math.max(totalDuration, getTrackDuration(dancer.headTrack));
		totalDuration = Math.max(totalDuration, getTrackDuration(dancer.torsoTrack));
		totalDuration = Math.max(totalDuration, getTrackDuration(dancer.legsTrack));
		totalDuration = Math.max(totalDuration, getTrackDuration(dancer.groundTrack));

		if (dancer.pathNodes.length > 0) {
			const lastPathNode = dancer.pathNodes[dancer.pathNodes.length - 1];
			totalDuration = Math.max(totalDuration, lastPathNode.time + lastPathNode.delay);
		}
	}

	return totalDuration;
}

const reducer = (state: ITimeline = initialState, action: Action): ITimeline => {
	switch (action.type) {
		case ActionType.INSERT_AUDIO_CLIP: {
			if (action.payload.duration <= 0) {
				console.error(
					'Trying to insert audio clip with duration of: ' + action.payload.duration
				);
				return state;
			}

			let nextAudioClipId = state.nextAudioClipId;

			let newClip: IAudioClip | null = null;

			let newState = mapAudioTracks(state, (trackId: ITrackId, audioTrack: IAudioTrack) => {
				if (audioTrack.muted) return audioTrack;

				if (
					action.payload.trackId !== null &&
					differentTracks(action.payload.trackId, trackId)
				)
					return audioTrack;

				newClip = {
					id: nextAudioClipId++,
					audioFileId: action.payload.audioFileId,
					duration: action.payload.duration,
					time: action.payload.time,
					trackId: trackId,
				};
				return { ...audioTrack, clips: [...audioTrack.clips, newClip] };
			});

			newState.nextAudioClipId = nextAudioClipId;
			newState.duration = calculateTotalDuration(newState);
			newState.selectedAudioClip = newClip;
			return newState;
		}
		case ActionType.SET_TRACK_MUTED: {
			let newState = mapAudioTracks(state, (trackId: ITrackId, audioTrack: IAudioTrack) => {
				if (!isTrackIdEqual(trackId, action.payload.trackId)) return audioTrack;
				return { ...audioTrack, muted: action.payload.muted };
			});
			return newState;
		}
		case ActionType.SET_ALL_TRACKS_MUTED: {
			let newState = mapAudioTracks(state, (trackId: ITrackId, audioTrack: IAudioTrack) => {
				return { ...audioTrack, muted: action.payload.muted };
			});
			return newState;
		}
		case ActionType.MOVE_AUDIO_CLIP: {
			let foundAudioClipToMove: IAudioClip | null = null;

			// Remove existing audioClip
			let newState = mapAudioTracks(state, (_: ITrackId, audioTrack: IAudioTrack) => {
				let filteredAudioClips = audioTrack.clips.filter((clip) => {
					if (clip.id !== action.payload.audioClipId) return true;

					if (foundAudioClipToMove !== null) {
						console.error('Multiple audioClips with same id found: ' + clip.id);
					}

					foundAudioClipToMove = clip;
					return false;
				});
				return { ...audioTrack, clips: filteredAudioClips };
			});

			// Check if audioClip was found
			if (foundAudioClipToMove === null) {
				console.error('Could not find audioClip wiht id: ' + action.payload.audioClipId);
				return state;
			}

			const audioClipToMove: IAudioClip = foundAudioClipToMove;

			// Insert audioClip in new spot
			newState = mapAudioTracks(newState, (trackId: ITrackId, audioTrack: IAudioTrack) => {
				if (!isTrackIdEqual(trackId, action.payload.trackId)) return audioTrack;
				return {
					...audioTrack,
					clips: [
						...audioTrack.clips,
						{ ...audioClipToMove, trackId: trackId, time: action.payload.time },
					],
				};
			});

			newState.duration = calculateTotalDuration(newState);
			return newState;
		}
		case ActionType.DELETE_AUDIO_CLIP: {
			let newState = mapAudioTracks(state, (trackId: ITrackId, audioTrack: IAudioTrack) => {
				return {
					...audioTrack,
					clips: audioTrack.clips.filter((clip) => clip.id !== action.payload.clipId),
				};
			});
			newState.duration = calculateTotalDuration(newState);
			newState.selectedAudioClip = null;
			return newState;
		}
		case ActionType.DELETE_AUDIO_CLIPS_AT_TIME: {
			const time = action.payload.time;
			let newState = mapAudioTracks(state, (trackId: ITrackId, audioTrack: IAudioTrack) => {
				if (audioTrack.muted) return audioTrack;

				const prevClipCount = audioTrack.clips.length;
				let newClips = audioTrack.clips.filter(
					(clip) => time >= clip.time + clip.duration || time < clip.time
				);

				if (newClips.length < prevClipCount) {
					return {
						...audioTrack,
						clips: newClips,
					};
				}

				// No audio clips were deleted, find an delete audio gap
				let gapStartTime = 0;
				let gapEndTime = Infinity;
				for (const clip of newClips) {
					const clipEndTime = clip.time + clip.duration;
					if (clipEndTime < time && clipEndTime > gapStartTime) {
						gapStartTime = clipEndTime;
					}
					if (clip.time > time && clip.time < gapEndTime) {
						gapEndTime = clip.time;
					}
				}

				if (!Number.isFinite(gapEndTime)) return audioTrack;

				const gapDuration = gapEndTime - gapStartTime;
				for (let i = 0; i < newClips.length; i++) {
					if (newClips[i].time > time) {
						newClips[i] = {
							...newClips[i],
							time: newClips[i].time - gapDuration,
						};
					}
				}

				return {
					...audioTrack,
					clips: newClips,
				};
			});
			newState.duration = calculateTotalDuration(newState);
			newState.selectedAudioClip = null;
			return newState;
		}
		case ActionType.DELETE_AUDIO_GAP: {
			let newState = mapAudioTracks(state, (trackId: ITrackId, audioTrack: IAudioTrack) => {
				const afterClip = audioTrack.clips.find(clip => clip.id === action.payload.afterClipId);
				if (afterClip === undefined) return audioTrack;

				const gapEndTime = afterClip.time;
				let gapStartTime = 0;
				for (const clip of audioTrack.clips) {
					if (clip.id === afterClip.id) continue;
					if (clip.time > gapEndTime) continue;
					gapStartTime = Math.max(gapStartTime, clip.time + clip.duration);
				}

				if (gapStartTime >= gapEndTime) return audioTrack;
				const offset = gapEndTime - gapStartTime;

				return {
					...audioTrack,
					clips: audioTrack.clips.map(clip => clip.time < gapStartTime ? clip : {...clip, time: clip.time - offset}),
				};
			});
			newState.duration = calculateTotalDuration(newState);
			newState.selectedAudioGap = null;
			return newState;

		}
		case ActionType.SELECT_AUDIO_CLIP: {
			return {
				...state,
				selectedAudioClip: action.payload.clip,
				selectedAudioGap: action.payload.clip !== null ? null : state.selectedAudioGap,
			};
		}
		case ActionType.SELECT_AUDIO_GAP: {
			return {
				...state,
				selectedAudioGap: action.payload,
				selectedAudioClip: action.payload !== null ? null : state.selectedAudioClip,
			};
		}
		case ActionType.SELECT_DANCER_PATH_NODE: {
			if (action.payload === null) {
				return { ...state, selectedDancerPathNode: null };
			}

			return {
				...state,
				selectedDancerPathNode: {
					dancerId: action.payload.dancerId,
					pathNodeId: action.payload.pathNodeId,
				},
			};
		}
		case ActionType.DELETE_AUDIO_FILE: {
			let newState = mapAudioTracks(state, (trackId: ITrackId, audioTrack: IAudioTrack) => {
				return {
					...audioTrack,
					clips: audioTrack.clips.filter(
						(clip) => clip.audioFileId !== action.payload.id
					),
				};
			});
			newState.duration = calculateTotalDuration(newState);
			return newState;
		}
		case ActionType.ADD_DANCER_PATH_NODE: {
			let newPathNodeId: number | null = null;
			let newDancers = state.dancers.map((dancer) => {
				if (dancer.id !== action.payload.dancerId) return dancer;
				let newPathNodes = dancer.pathNodes;
				// If there is a node with the same time, add 0.01 secs to all following nodes
				if (newPathNodes.some((node) => { return node.time === action.payload.time })) {
					newPathNodes = newPathNodes.map((node) => {
						return {
							...node,
							time: node.time >= action.payload.time ? node.time + 0.01 : node.time
						}
					});
				}
				newPathNodes = [
					...newPathNodes,
					{
						id: dancer.nextPathNodeId,
						name: `Node ${dancer.nextPathNodeId}`,
						time: action.payload.time,
						delay: action.payload.delay,
						pos: action.payload.pos,
					}
				];
				
				newPathNodeId = dancer.nextPathNodeId;
				return {
					...dancer,
					pathNodes: fixDancerPathNodesArray(newPathNodes),
					nextPathNodeId: dancer.nextPathNodeId + 1,
				};
			});

			let newState = {
				...state,
				dancers: newDancers,
			};
			newState.duration = calculateTotalDuration(newState);
			if (newPathNodeId !== null) {
				newState.selectedDancerPathNode = {
					dancerId: action.payload.dancerId,
					pathNodeId: newPathNodeId,
				};
			}
			return newState;
		}
		case ActionType.SET_DANCER_PATH_NODE: {
			let newDancers = state.dancers.map((dancer) => {
				if (dancer.id !== action.payload.dancerId) return dancer;
				let newPathNodes = dancer.pathNodes.map((pathNode) => {
					if (pathNode.id !== action.payload.pathNodeId) return pathNode;
					return {
						...pathNode,
						name: action.payload.name,
						time: action.payload.time !== undefined
								? action.payload.time
								: pathNode.time,
						delay:
							action.payload.delay !== undefined
								? action.payload.delay
								: pathNode.delay,
						pos: action.payload.pos,
					};
				});
				return {
					...dancer,
					pathNodes: fixDancerPathNodesArray(newPathNodes),
				};
			});
			let newState = {
				...state,
				dancers: newDancers,
			};
			newState.duration = calculateTotalDuration(newState);
			return newState;
		}
		case ActionType.SET_DANCER_PATH_NODE_DURATION: {
			let newDancers = state.dancers.map((dancer) => {
				if (dancer.id !== action.payload.dancerId) return dancer;

				let timeOffset = 0;
				let newPathNodes = dancer.pathNodes.map((pathNode, index) => {
					if (pathNode.id === action.payload.pathNodeId) {
						console.assert(timeOffset === 0);
						if (index >= dancer.pathNodes.length - 1) return pathNode;
						let nextPathNode = dancer.pathNodes[index + 1];
						timeOffset = action.payload.duration - (nextPathNode.time - pathNode.time);
						return pathNode;
					}

					if (timeOffset === 0) return pathNode;

					return {
						...pathNode,
						time: pathNode.time + timeOffset,
					};
				});
				return {
					...dancer,
					pathNodes: fixDancerPathNodesArray(newPathNodes),
				};
			});
			let newState = {
				...state,
				dancers: newDancers,
			};
			newState.duration = calculateTotalDuration(newState);
			return newState;

		}
		case ActionType.DELETE_DANCER_PATH_NODE: {
			let newDancers = state.dancers.map((dancer) => {
				if (dancer.id !== action.payload.dancerId) return dancer;
				let newPathNodes = dancer.pathNodes.filter(
					(pathNode) => pathNode.id !== action.payload.pathNodeId
				);
				return {
					...dancer,
					pathNodes: newPathNodes,
				};
			});
			let newState = {
				...state,
				dancers: newDancers,
			};
			newState.duration = calculateTotalDuration(newState);
			return newState;
		}
		case ActionType.MOVE_FUTURE_DANCER_PATH_NODE: {
			let newDancers = state.dancers.map((dancer) => {
				if (dancer.id !== action.payload.dancerId) return dancer;

				let maybeFirstPathNode = dancer.pathNodes.find(
					(pathNode) => pathNode.id === action.payload.firstPathNodeId
				);
				if (maybeFirstPathNode === undefined) return dancer;
				const firstPathNode = maybeFirstPathNode;

				let newPathNodes = dancer.pathNodes.map((pathNode) => {
					if (pathNode.time < firstPathNode.time) return pathNode;
					return { ...pathNode, time: pathNode.time + action.payload.timeOffset };
				});

				return {
					...dancer,
					pathNodes: fixDancerPathNodesArray(newPathNodes),
				};
			});
			let newState = {
				...state,
				dancers: newDancers,
			};
			newState.duration = calculateTotalDuration(newState);
			return newState;
		}
		case ActionType.ADD_DANCER: {
			const newDancer: IDancer = {
				id: state.nextDancerId,
				name: 'Dancer ' + state.nextDancerId,
				height: feetToCentimeters(5.5),
				pathNodeShape: DancerPathNodeShape.Triangle,
				pathColor: DancerPathColor.Red,
				pathNodes: [],
				nextPathNodeId: 1,
				aboveHeadTrack: {
					muted: false,
					clips: [],
				},
				headTrack: {
					muted: false,
					clips: [],
				},
				torsoTrack: {
					muted: false,
					clips: [],
				},
				legsTrack: {
					muted: false,
					clips: [],
				},
				groundTrack: {
					muted: false,
					clips: [],
				},
			};
			return {
				...state,
				dancers: state.dancers.concat(newDancer),
				nextDancerId: state.nextDancerId + 1,
				selectedDancerId: newDancer.id,
			};
		}
		case ActionType.DELETE_DANCER: {
			let newState = {
				...state,
				dancers: state.dancers.filter((dancer) => dancer.id !== action.payload.dancerId),
			};
			newState.duration = calculateTotalDuration(newState);
			return newState;
		}
		case ActionType.SET_DANCER_NAME: {
			let newDancers = state.dancers.map((dancer) => {
				if (dancer.id !== action.payload.id) return dancer;
				return {
					...dancer,
					name: action.payload.name,
				};
			});
			return {
				...state,
				dancers: newDancers,
			};
		}
		case ActionType.SET_DANCER_PATH_APPEARANCE: {
			let newDancers = state.dancers.map((dancer) => {
				if (dancer.id !== action.payload.id) return dancer;
				return {
					...dancer,
					pathColor: action.payload.color,
					pathNodeShape: action.payload.shape,
				};
			});
			return {
				...state,
				dancers: newDancers,
			};
		}
		case ActionType.SET_DANCER_HEIGHT: {
			let newDancers = state.dancers.map((dancer) => {
				if (dancer.id !== action.payload.id) return dancer;
				return {
					...dancer,
					height: action.payload.height,
				};
			});
			return {
				...state,
				dancers: newDancers,
			};
		}
		case ActionType.SET_SELECTED_DANCER: {
			if (action.payload.id !== null && state.dancers.find((dancer) => dancer.id === action.payload.id) === undefined) {
				console.error("Could not find Dancer with id: " + action.payload.id);
				return state;
			}
			return {
				...state,
				selectedDancerId: action.payload.id,
			}
		}
		case ActionType.RESET_STATE: {
			return initialState;
		}
		default:
			return state;
	}
};

export default reducer;
